import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ChevronRight, 
  ChevronDown, 
  LayoutDashboard,
  Users,
  Wallet,
  TrendingUp,
  Play,
  BookOpen,
  User,
  HelpCircle,
  Settings,
  UserCog,
  GitBranch,
  DollarSign,
  Percent,
  Shield,
  Trophy,
  FileText,
  LogOut,
  Newspaper
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { isAlternateDomain } from '@/utils/domainUtils';
import { getLocalizedPath } from '@/utils/languageUtils';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { openExternal } from '@/utils/openExternal';

interface SidebarProps {
  userRole: 'user' | 'admin';
  maxLevels?: number | null;
}

interface SectionConfig {
  key: string;
  icon: React.ComponentType<any>;
  items?: Array<{
    key: string;
    path?: string;
    external?: boolean;
    url?: string;
  }>;
  path?: string; // Direct path for sections without subitems
}

const BackofficeSidebar = ({ userRole, maxLevels }: SidebarProps) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const { signOut } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const userSections: SectionConfig[] = [
    { key: 'dashboard', icon: LayoutDashboard, path: '/dashboard' }, // Direct link without subitems
    { 
      key: 'myCommunity', 
      icon: Users, 
      items: [
        { key: 'genealogyTree', path: '/my-community/genealogy-tree' },
        { key: 'communityMembers', path: '/my-community/members' }
      ]
    },
    { 
      key: 'investmentAccounts', 
      icon: Wallet, 
      items: [
        { key: 'myAccounts', path: '/investment-accounts' },
        { key: 'addAccount', path: '/add-account' }
      ]
    },
    {
      key: 'myProfits',
      icon: TrendingUp,
      items: [
        { key: 'accountsProfitSummary', path: '/my-profits/accounts-summary' },
        { key: 'accountsProfitHistory', path: '/my-profits/accounts-history' },
        { key: 'commissionsSummary', path: '/my-profits/commissions-summary' },
        { key: 'commissionsHistory', path: '/my-profits/commissions-history' },
        { key: 'commissionsPaymentHistory', path: '/my-profits/commissions-payment-history' },
        { key: 'myPaymentMethods', path: '/profile/payment-methods' }
      ]
    },
    {
      key: 'startInvesting',
      icon: Play,
      items: (() => {
        const isAltDomain = isAlternateDomain();
        return [
          { key: 'createRadexAccount', external: true, url: 'https://my.radexmarkets.com/en/mycp/pl_login/signup?Ic=NSQEB&s=r6' },
          { key: 'joinPammInstitutional', external: true, url: isAltDomain ? 'https://pamm.radexmarkets.com/app/join/122/msdchvtn' : 'https://pamm.radexmarkets.com/app/join/74/gzcdqqcw' },
          { key: 'joinPammSpartanAi', external: true, url: isAltDomain ? 'https://pamm.radexmarkets.com/app/join/116/lgdjbtub' : 'https://pamm.radexmarkets.com/app/join/116/cptpxwso' },
          { key: 'accessPammAccount', external: true, url: 'https://pamm.radexmarkets.com/app/auth/investor' }
        ];
      })()
    },
    { key: 'tutorials', icon: BookOpen, path: '/tutorials' },
    {
      key: 'myProfile',
      icon: User,
      items: [
        { key: 'theClubUserProfile', path: '/profile/the-club-user-profile' },
        { key: 'personalInformation', path: '/profile/personal-info' },
        { key: 'paymentMethods', path: '/profile/payment-methods' },
        { key: 'referralLink', path: '/profile/referral-link' }
      ]
    },
    { key: 'support', icon: HelpCircle, items: [{ key: 'helpCenter', path: '/contact-us' }] }
  ];

  const adminSections: SectionConfig[] = [
    { key: 'dashboard', icon: LayoutDashboard, path: '/admin-dashboard' }, // Direct link without subitems
    {
      key: 'userManagement',
      icon: Users,
      items: [
        { key: 'usersDashboard', path: '/admin/users' },
        { key: 'searchUser', path: '/admin/users/search' },
      ]
    },
    {
      key: 'theClubCommunity',
      icon: GitBranch,
      items: [
        { key: 'communityGenealogyTree', path: '/admin/community/genealogy-tree' },
        { key: 'communityLevels', path: '/admin/community/levels' },
        { key: 'communityTreeIntegrity', path: '/admin/community/integrity' }
      ]
    },
    {
      key: 'tradingAccounts',
      icon: DollarSign,
      items: [
        { key: 'tradingAccounts', path: '/admin/accounts' },
        { key: 'tradingAccountsProfit', path: '/admin/accounts/profits' },
        { key: 'tradingAccountsProfitsTotals', path: '/admin/accounts/profits-totals' },
        { key: 'tradingAccountsProfitRegister', path: '/admin/accounts/profits-registration' },
        { key: 'tradingAccountsBalanceUpdate', path: '/admin/accounts/balance-update' },
        { key: 'tradingAccountsAutoRegister', path: '/admin/accounts/auto-account-register' }
      ]
    },
    {
      key: 'commissions',
      icon: Percent,
      items: [
        { key: 'commissionsTracker', path: '/admin/commissions/tracker' },
        { key: 'commissionsProfits', path: '/admin/commissions/profits' },
        { key: 'commissionsTotals', path: '/admin/commissions/totals' },
        { key: 'commissionsBalances', path: '/admin/commissions/balances' },
        { key: 'commissionsPayments', path: '/admin/commissions/payments' },
        { key: 'commissionsManager', path: '/admin/commissions/manager' },
        { key: 'commissionsPayoutManager', path: '/admin/commissions/payout-manager' },
        { key: 'commissionsModels', path: '/admin/commissions/models' },
      ]
    },
    {
      key: 'systemUserManagement',
      icon: UserCog,
      items: [
        { key: 'systemUsersDashboard', path: '/admin/system-users' },
        { key: 'systemUserSearch', path: '/admin/system-users/search' },
        { key: 'systemUserEdit', path: '/admin/system-users/edit' }
      ]
    },
    {
      key: 'reports',
      icon: FileText,
      items: [
        { key: 'profitReports', path: '/admin/reports/profits' },
        { key: 'commissionReports', path: '/admin/reports/commissions' },
        { key: 'referralReports', path: '/admin/reports/referrals' },
        { key: 'topUsersReports', path: '/admin/reports/top-users' }
      ]
    },
    {
      key: 'news',
      icon: Newspaper,
      items: [
        { key: 'createPost', path: '/admin/news/create-post' }
      ]
    }
  ];

  // Filter sections based on maxLevels (hide community/referral for 0-level users)
  const isZeroLevel = maxLevels === 0;
  
  let filteredSections = userRole === 'admin' ? adminSections : userSections;
  
  if (userRole === 'user' && isZeroLevel) {
    const hiddenItems = new Set(['referralLink', 'paymentMethods']);
    const hiddenProfitItems = new Set(['commissionsSummary', 'commissionsHistory', 'commissionsPaymentHistory', 'myPaymentMethods']);
    filteredSections = filteredSections
      .filter(s => s.key !== 'myCommunity')
      .map(s => {
        if (s.key === 'myProfile' && s.items) {
          return { ...s, items: s.items.filter(i => !hiddenItems.has(i.key)) };
        }
        if (s.key === 'myProfits' && s.items) {
          return { ...s, items: s.items.filter(i => !hiddenProfitItems.has(i.key)) };
        }
        return s;
      });
  }

  const sections = filteredSections;
  const translationPrefix = userRole === 'admin' ? 'sidebarAdmin' : 'sidebarUser';

  // Handle mouse leave from entire sidebar to collapse all sections
  const handleSidebarMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setExpandedSection(null);
  };

  // Handle section hover with reduced timeout
  const handleMouseEnter = (sectionKey: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    hoverTimeoutRef.current = setTimeout(() => {
      setExpandedSection(sectionKey);
    }, 800);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSection(prev => (prev === sectionKey ? null : sectionKey));
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleItemClick = (item: any) => {
    if (item.external && item.url) {
      openExternal(item.url);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={sidebarRef}
      onMouseLeave={handleSidebarMouseLeave}
      className="w-80 bg-gradient-backoffice flex flex-col h-screen shadow-sharp-lg"
    >
      {/* Logo Section */}
      <div className="p-6 border-b border-white/10 flex justify-center">
        <Link 
          to="/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="block"
        >
          <img 
            src="/logo_1_inverted_no_background.png" 
            alt="TheClub Logo"
            className="h-20 w-auto mx-auto"
          />
        </Link>
      </div>

      {/* Navigation Sections */}
      <div className="flex-1 overflow-y-auto py-4">
        {sections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSection === section.key;
          const hasItems = section.items && section.items.length > 0;
          
          return (
            <div key={section.key} className="mb-2">
              {/* Direct link for sections without items, expandable for sections with items */}
              {!hasItems ? (
                <Link
                  to={getLocalizedPath(section.path!, language)}
                  className="w-full px-6 py-3 flex items-center gap-3 text-left hover:bg-white/10 transition-all duration-200 group"
                >
                  <Icon className="h-5 w-5 text-white group-hover:text-white transition-colors" />
                  <span className="text-white font-medium group-hover:text-white transition-colors">
                    {t(`backoffice.${translationPrefix}.sections.${section.key}.title`)}
                  </span>
                </Link>
              ) : (
                <>
                  <button
                    onClick={() => toggleSection(section.key)}
                    onMouseEnter={() => handleMouseEnter(section.key)}
                    onMouseLeave={handleMouseLeave}
                    className="w-full px-6 py-3 flex items-center justify-between text-left hover:bg-white/10 transition-all duration-200 group"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-white group-hover:text-white transition-colors" />
                      <span className="text-white font-medium group-hover:text-white transition-colors">
                        {t(`backoffice.${translationPrefix}.sections.${section.key}.title`)}
                      </span>
                    </div>
                    <div className={`transition-transform duration-300 ease-in-out ${
                      isExpanded ? 'rotate-90' : 'rotate-0'
                    }`}>
                      <ChevronRight className="h-4 w-4 text-white/70 group-hover:text-white transition-colors" />
                    </div>
                  </button>

                  {/* Animated collapsible content */}
                  <div className={`overflow-hidden duration-300 ease-in-out ${
                    isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="bg-black/20 border-l-2 border-white/30 ml-6 transform transition-transform duration-300 ease-in-out">
                      {section.items!.map((item) => (
                        item.external && item.url ? (
                          <a
                            key={item.key}
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full py-2 px-6 text-left text-base text-white/80 hover:text-white hover:bg-white/5 transition-all duration-200 transform hover:translate-x-1"
                          >
                            {t(`backoffice.${translationPrefix}.sections.${section.key}.items.${item.key}`)}
                          </a>
                        ) : item.path ? (
                          <Link
                            key={item.key}
                            to={getLocalizedPath(item.path, language)}
                            className="block w-full py-2 px-6 text-left text-base text-white/80 hover:text-white hover:bg-white/5 transition-all duration-200 transform hover:translate-x-1"
                          >
                            {t(`backoffice.${translationPrefix}.sections.${section.key}.items.${item.key}`)}
                          </Link>
                        ) : (
                          <button
                            key={item.key}
                            onClick={() => handleItemClick(item)}
                            className="w-full py-2 px-6 text-left text-base text-white/80 hover:text-white hover:bg-white/5 transition-all duration-200 transform hover:translate-x-1"
                          >
                            {t(`backoffice.${translationPrefix}.sections.${section.key}.items.${item.key}`)}
                          </button>
                        )
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Sign Out Button */}
      <div className="p-4 border-t border-white/10">
        <button
          onClick={handleSignOut}
          className="w-full px-6 py-3 flex items-center gap-3 text-left hover:bg-white/10 transition-all duration-200 group rounded-sm"
        >
          <LogOut className="h-5 w-5 text-white group-hover:text-red-300 transition-colors" />
          <span className="text-white font-medium group-hover:text-red-300 transition-colors">
            {t(`backoffice.${translationPrefix}.signOut`)}
          </span>
        </button>
      </div>
    </div>
  );
};

export default BackofficeSidebar;
